import { useBookLessonSummaries } from "@/lib/hooks/bookLessons/useBookLessons";
import { Button } from "@ai-teach/ui-web";
import { type BookScope, BookScopePicker } from "./BookScopePicker";

export function BookLessonMultiPicker({
  scope,
  selectedIds,
  onScopeChange,
  onSelectionChange,
}: {
  scope: BookScope;
  selectedIds: string[];
  onScopeChange: (scope: BookScope) => void;
  onSelectionChange: (ids: string[]) => void;
}) {
  const { bookLessons, loading } = useBookLessonSummaries(scope.seriesSlug);

  const inScope = bookLessons
    .filter(
      (one) =>
        (scope.levelId === null || one.levelId === scope.levelId) &&
        (scope.componentId === null || one.componentId === scope.componentId)
    )
    .sort((a, b) => a.lesson - b.lesson);

  const isReady = scope.levelId !== null && scope.componentId !== null;
  const selected = new Set(selectedIds);

  const toggle = (id: string) =>
    onSelectionChange(
      selected.has(id) ? selectedIds.filter((one) => one !== id) : [...selectedIds, id]
    );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <BookScopePicker idPrefix="batch" value={scope} onChange={onScopeChange} />
      </div>

      {!isReady ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-700">
          Choose a series, level and component to list its lessons.
        </p>
      ) : loading ? (
        <p className="text-sm text-gray-500">Loading lessons…</p>
      ) : inScope.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:border-gray-700">
          This level and component has no lessons.
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {selectedIds.length} of {inScope.length} selected
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onSelectionChange(inScope.map((one) => one.id))}
              >
                Select all
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={selectedIds.length === 0}
                onClick={() => onSelectionChange([])}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="grid max-h-64 grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-2 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-800">
            {inScope.map((one) => (
              <label
                key={one.id}
                className={`flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm ring-1 transition-colors ${
                  selected.has(one.id)
                    ? "bg-accent-500/10 text-accent-500 ring-accent-500"
                    : "ring-transparent hover:bg-black/5 dark:hover:bg-white/5"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(one.id)}
                  onChange={() => toggle(one.id)}
                  className="h-4 w-4 accent-accent-500"
                />
                {one.printedLabel}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
